import type { ComponentType, SVGProps } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../auth/AuthContext";
import { canSeeBilling, canSeeFleet } from "../auth/roleView";
import { DevelopIcon, FleetIcon, GovernIcon, HomeIcon, OperateIcon, SettingsIcon } from "./navIcons";

/**
 * IA slice 1 (docs/product/ui-information-architecture.md §3): the sidebar
 * is six destinations organized by the JOB a persona does, not by our data
 * model — Home / Develop / Operate / Fleet / Govern, plus a pinned
 * Settings/Account entry at the bottom. Replaces the old ~20-item,
 * data-model-shaped nav (Projects, Gallery, Environments, Hosts, Provider
 * credentials, Import, Routes, Secrets, My services, Containers, Health,
 * Alerts, Workbench, Bonnie, LLM credentials, Users, SSO, Audit log,
 * Governance, Billing all as separate top-level links).
 *
 * Slice 1 intentionally does NOT build new consolidated destination pages
 * (that's slices 2-5) — each destination just LANDS on the best existing
 * page for that job, and its "active" highlight lights for every OLD route
 * the RFC assigns to that destination (so navigating deep into e.g.
 * `/projects/:id/corpus` still shows Develop as the current section) even
 * though most of those old routes have no direct link in this sidebar
 * anymore. They stay reachable via the destination's landing page or
 * in-page links — see the RFC's "what each destination absorbs" table.
 */

interface Destination {
  id: string;
  label: string;
  /** Where the nav link itself points. */
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Every path prefix this destination's section owns for active-state
   * purposes (see `isDestinationActive`) — a superset of `to` covering the
   * old routes the RFC assigns to this destination, even where slice 1
   * hasn't built a direct link to all of them yet. */
  prefixes: string[];
}

const HOME_DESTINATION: Destination = { id: "home", label: "Home", to: "/", icon: HomeIcon, prefixes: ["/"] };

const DESTINATIONS: Destination[] = [
  { id: "develop", label: "Develop", to: "/projects", icon: DevelopIcon, prefixes: ["/projects", "/gallery", "/import", "/agent-sessions", "/bonnie"] },
  // "/containers" stays in Operate's prefix set even though it's no longer a nav
  // destination of its own (IA slice 6 retired the fleet-wide browser) — a container's
  // detail page (`/containers/:nodeId/:containerId`) is still real and reachable from a
  // service's Operate view, so it should still light Operate when visited that way.
  { id: "operate", label: "Operate", to: "/operate", icon: OperateIcon, prefixes: ["/operate", "/containers"] },
  { id: "fleet", label: "Fleet", to: "/hosts", icon: FleetIcon, prefixes: ["/hosts", "/environments", "/routes", "/instances"] },
  {
    id: "govern",
    label: "Govern",
    to: "/govern",
    icon: GovernIcon,
    // "/govern" alone covers every Govern tab route too (they all live under it —
    // GovernPage.tsx). IA slice 6 renamed this root from `/admin/audit` and retired the
    // pre-slice-5 routes (`/admin/governance`, `/secrets`, `/provider-credentials`,
    // `/llm-credentials`) outright — no alias left to list here.
    prefixes: ["/govern"],
  },
];

/** Matches by prefix, same rule the old per-link nav used: exact match, or
 * the location continues past the prefix with a `/` (so `/projects` doesn't
 * also light up for an unrelated `/projects-archive`-shaped route). */
function pathMatches(location: string, prefix: string): boolean {
  return location === prefix || location.startsWith(`${prefix}/`);
}

function DestinationLink({ destination, location }: { destination: Destination; location: string }) {
  const isActive = destination.prefixes.some((prefix) => pathMatches(location, prefix));
  const Icon = destination.icon;
  return (
    <li>
      <Link
        href={destination.to}
        className={`nav-sidebar__link${isActive ? " is-active" : ""}`}
        aria-current={isActive ? "page" : undefined}
      >
        <Icon className="nav-sidebar__link-icon" />
        <span>{destination.label}</span>
      </Link>
    </li>
  );
}

function PinnedLink({
  to,
  label,
  icon: Icon,
  location,
}: {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  location: string;
}) {
  const isActive = pathMatches(location, to);
  return (
    <Link
      href={to}
      className={`nav-sidebar__link nav-sidebar__pinned${isActive ? " is-active" : ""}`}
      aria-current={isActive ? "page" : undefined}
    >
      <Icon className="nav-sidebar__link-icon" />
      <span>{label}</span>
    </Link>
  );
}

interface NavSidebarProps {
  /** Narrow-viewport drawer state (useNavDrawer) — adds `.is-open`, which
   * shell.css only gives meaning to below the mobile breakpoint. */
  isOpen?: boolean;
}

export function NavSidebar({ isOpen = false }: NavSidebarProps) {
  const [location] = useLocation();
  const { session } = useAuth();
  const isAdmin = session?.role === "admin";
  // Same "hidden, not greyed" coherence rule as before (S-8 — this is UX
  // only, the server enforces the real decision on every request): a role
  // that holds no fleet grants (billing_admin) never sees fleet-shaped nav
  // it can only ever 403 on.
  const showFleetSections = canSeeFleet(session?.role);
  const showBilling = canSeeBilling(session?.role);

  // Admin sees all four fleet-shaped destinations; the App Developer seat
  // (operator/viewer) sees only the two whose job is theirs (Develop,
  // Operate) — Fleet and Govern are the Platform Lead's control room and
  // the "prove it to the auditor" surface, neither of which a role without
  // audit_log:view/host management grants can act in (RFC §3 "role-aware
  // trimming").
  const destinations = showFleetSections ? (isAdmin ? DESTINATIONS : DESTINATIONS.filter((d) => d.id === "develop" || d.id === "operate")) : [];

  return (
    <nav
      id="primary-navigation"
      className={`nav-sidebar${isOpen ? " is-open" : ""}`}
      aria-label="Primary"
    >
      {showFleetSections && (
        <ul className="nav-sidebar__destinations">
          <DestinationLink destination={HOME_DESTINATION} location={location} />
          {destinations.map((destination) => (
            <DestinationLink key={destination.id} destination={destination} location={location} />
          ))}
        </ul>
      )}

      <div className="nav-sidebar__spacer" />

      {/* Pinned below a divider, mirroring the mocks: admin gets "Settings"
       * (the consolidated `/settings` page — users/SSO/billing/account, IA
       * slice 5), the App Developer seat gets "Account" (same page, its one
       * visible tab), and billing_admin — who never sees the six destinations
       * above and whose "/" lands straight on /billing (RoleLanding), not
       * Home — keeps the one link it has always had, "Billing". */}
      <div className="nav-sidebar__pinned-group">
        {isAdmin && <PinnedLink to="/settings" label="Settings" icon={SettingsIcon} location={location} />}
        {!isAdmin && showFleetSections && <PinnedLink to="/settings" label="Account" icon={SettingsIcon} location={location} />}
        {!showFleetSections && showBilling && <PinnedLink to="/billing" label="Billing" icon={SettingsIcon} location={location} />}
      </div>
    </nav>
  );
}
